import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2, CheckCircle2, XCircle, Eye, MapPin, Phone } from 'lucide-react';
import { UniversitiesTableProps } from '../types';

export function UniversitiesTable({
  isDark,
  universities,
  isLoading,
  onEdit,
  onDelete,
  onViewDirections,
}: UniversitiesTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            University
          </TableHead>
          <TableHead className={cn('font-semibold hidden md:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Region
          </TableHead>
          <TableHead className={cn('font-semibold hidden lg:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Phone
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Directions
          </TableHead>
          <TableHead className={cn('font-semibold hidden sm:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Status
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Actions
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {universities.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-12 text-slate-500">
              No universities found
            </TableCell>
          </TableRow>
        ) : (
          universities.map((university) => (
            <TableRow
              key={university.id}
              className={cn(
                'transition-colors',
                isDark
                  ? 'border-slate-700 hover:bg-slate-800/50'
                  : 'border-slate-100 hover:bg-slate-50'
              )}
            >
              <TableCell>
                <div>
                  <p className={cn(
                    'font-medium text-sm',
                    isDark ? 'text-white' : 'text-[#0F172A]'
                  )}>{university.name}</p>
                  {university.location && (
                    <p className={cn(
                      'text-xs mt-0.5 flex items-center gap-1',
                      isDark ? 'text-slate-500' : 'text-slate-400'
                    )}>
                      <MapPin className="w-3 h-3" />
                      {university.location.length > 60
                        ? university.location.substring(0, 60) + '...'
                        : university.location}
                    </p>
                  )}
                </div>
              </TableCell>
              <TableCell className={cn(
                'hidden md:table-cell',
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>
                {university.region || '—'}
              </TableCell>
              <TableCell className={cn(
                'hidden lg:table-cell',
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>
                {university.phone ? (
                  <span className="flex items-center gap-1 text-sm">
                    <Phone className="w-3 h-3" />
                    {university.phone}
                  </span>
                ) : '—'}
              </TableCell>
              <TableCell>
                <Badge
                  className="bg-[#0EA5A4]/10 text-[#0EA5A4] border-0 cursor-pointer hover:bg-[#0EA5A4]/20"
                  onClick={() => onViewDirections(university)}
                >
                  {(university as any).directions_count ?? '...'} directions
                </Badge>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  university.is_active
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-slate-500/10 text-slate-500'
                )}>
                  {university.is_active ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" />Active</>
                  ) : (
                    <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                  )}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDirections(university)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-[#0EA5A4]'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-[#0EA5A4]'
                    )}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(university)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(university.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-red-600'
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
